/**
 * Order Assignment — Admin dashboard
 *
 * Two-column manual order assignment workflow:
 *   Left:  Unassigned orders pool (priority orders surface first)
 *   Right: Agents with their current + queue load (max 50)
 *
 * Replaces the previous automatic agent-assignment flow. Admin explicitly
 * assigns every order; reassign/unassign from either column.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Package,
  User,
  Truck,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Clock,
  CalendarDays,
  ArrowRight,
  XCircle,
  IndianRupee,
} from 'lucide-react';
import deliveryAgentService from '../../services/deliveryAgentService';

const MAX_PER_AGENT = 50;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const OrderAssignment = ({ newCount = 0 }) => {
  const [unassigned, setUnassigned] = useState([]);
  const [highlightActive, setHighlightActive] = useState(false);

  useEffect(() => {
    if (newCount > 0) {
      setHighlightActive(true);
      const t = setTimeout(() => setHighlightActive(false), 3000);
      return () => clearTimeout(t);
    }
  }, [newCount]);
  const [agents, setAgents] = useState([]);
  const [agentLoads, setAgentLoads] = useState({});  // agentId -> { currentOrder, queue, used }
  const [loadingPool, setLoadingPool] = useState(true);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Per-order assignment dropdown state
  const [assignPickerOrderId, setAssignPickerOrderId] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [actingOnOrderId, setActingOnOrderId] = useState(null);

  // Per-queue-row reassign dropdown state
  const [reassignPickerOrderId, setReassignPickerOrderId] = useState(null);
  const [reassignTargetId, setReassignTargetId] = useState('');

  const showSuccess = (msg) => {
    setSuccess(msg);
    setError('');
    setTimeout(() => setSuccess(''), 3000);
  };

  const showError = (msg) => {
    setError(msg);
    setSuccess('');
  };

  const fetchUnassigned = useCallback(async () => {
    setLoadingPool(true);
    const res = await deliveryAgentService.getUnassignedOrders();
    if (res.success) {
      setUnassigned(res.data || []);
    } else {
      showError(res.error);
    }
    setLoadingPool(false);
  }, []);

  const fetchAgentsAndLoads = useCallback(async () => {
    setLoadingAgents(true);
    const agentsRes = await deliveryAgentService.getAll();
    if (!agentsRes.success) {
      showError(agentsRes.error);
      setLoadingAgents(false);
      return;
    }
    const activeAgents = (agentsRes.data || []).filter(a => a.status === 'active');
    setAgents(activeAgents);

    const loadPromises = activeAgents.map(a =>
      deliveryAgentService.getAgentLoad(a.id).then(r => ({ id: a.id, load: r.success ? r.data : null }))
    );
    const loadResults = await Promise.all(loadPromises);
    const loadsMap = {};
    loadResults.forEach(({ id, load }) => { if (load) loadsMap[id] = load; });
    setAgentLoads(loadsMap);
    setLoadingAgents(false);
  }, []);

  useEffect(() => {
    fetchUnassigned();
    fetchAgentsAndLoads();
  }, [fetchUnassigned, fetchAgentsAndLoads]);

  const refreshAll = () => {
    fetchUnassigned();
    fetchAgentsAndLoads();
  };

  const handleAssign = async (orderId, agentId) => {
    if (!agentId) {
      showError('Pick an agent first');
      return;
    }
    setActingOnOrderId(orderId);
    const res = await deliveryAgentService.assignOrder(orderId, Number(agentId));
    if (res.success) {
      showSuccess(`Order assigned`);
      setAssignPickerOrderId(null);
      setSelectedAgentId('');
      refreshAll();
    } else {
      showError(res.error);
    }
    setActingOnOrderId(null);
  };

  const handleReassign = async (orderId, newAgentId) => {
    if (!newAgentId) {
      showError('Pick an agent first');
      return;
    }
    setActingOnOrderId(orderId);
    const res = await deliveryAgentService.reassignOrder(orderId, Number(newAgentId));
    if (res.success) {
      showSuccess(`Order reassigned`);
      setReassignPickerOrderId(null);
      setReassignTargetId('');
      refreshAll();
    } else {
      showError(res.error);
    }
    setActingOnOrderId(null);
  };

  const handleUnassign = async (orderId) => {
    if (!globalThis.confirm(`Unassign order #${orderId}? It will go back to the unassigned pool.`)) return;
    setActingOnOrderId(orderId);
    const res = await deliveryAgentService.unassignOrder(orderId);
    if (res.success) {
      showSuccess(`Order #${orderId} unassigned`);
      refreshAll();
    } else {
      showError(res.error);
    }
    setActingOnOrderId(null);
  };

  // ── Render helpers ──────────────────────────────────────────────────────

  const renderAgentDropdown = (selectedValue, onChange, hideAgentId = null) => (
    <select
      value={selectedValue}
      onChange={(e) => onChange(e.target.value)}
      className="px-2 py-1 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
    >
      <option value="">— Select agent —</option>
      {agents
        .filter(a => a.id !== hideAgentId)
        .map(a => {
          const load = agentLoads[a.id];
          const used = load ? load.used : 0;
          const full = used >= MAX_PER_AGENT;
          return (
            <option key={a.id} value={a.id} disabled={full}>
              {a.name} — {used}/{MAX_PER_AGENT}{full ? ' (full)' : ''}
            </option>
          );
        })}
    </select>
  );

  const renderUnassignedOrder = (order, index) => {
    const isPriority = order.priority_at != null;
    const isNew = highlightActive && index < newCount;
    let cardClass = 'border-gray-200 bg-white';
    if (isNew) cardClass = 'border-orange-400 ring-2 ring-orange-300 bg-orange-50';
    else if (isPriority) cardClass = 'border-red-300 bg-red-50';
    return (
      <div
        key={order.id}
        className={`p-3 rounded-lg border transition-colors ${cardClass}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-mono text-sm font-semibold text-gray-800">
                #{order.id} · {order.orderNumber || '—'}
              </span>
              {isPriority && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-200 text-red-800 font-medium">
                  Priority
                </span>
              )}
              {order.deliveryType === 'scheduled' ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" /> Scheduled
                  {order.deliveryDate && ` · ${formatDate(order.deliveryDate)}`}
                </span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                  On-Demand
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 truncate">{order.shippingAddress}</p>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <IndianRupee className="w-3 h-3" />{Number(order.totalAmount || 0).toFixed(2)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />{formatDateTime(order.createdAt)}
              </span>
            </div>
          </div>
        </div>

        {assignPickerOrderId === order.id ? (
          <div className="mt-3 flex items-center gap-2">
            {renderAgentDropdown(selectedAgentId, setSelectedAgentId)}
            <button
              onClick={() => handleAssign(order.id, selectedAgentId)}
              disabled={actingOnOrderId === order.id || !selectedAgentId}
              className="px-3 py-1 bg-[#548B2E] text-white rounded-lg text-sm hover:bg-[#3f6b20] disabled:opacity-50 flex items-center gap-1"
            >
              {actingOnOrderId === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              Assign
            </button>
            <button
              onClick={() => { setAssignPickerOrderId(null); setSelectedAgentId(''); }}
              className="p-1 text-gray-500 hover:text-gray-700"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => { setAssignPickerOrderId(order.id); setSelectedAgentId(''); }}
            className="mt-3 inline-flex items-center gap-1 px-3 py-1 bg-[#548B2E]/10 text-[#2e4e18] rounded-lg text-sm hover:bg-[#548B2E]/20"
          >
            <ArrowRight className="w-3 h-3" /> Assign to…
          </button>
        )}
      </div>
    );
  };

  const renderAgentQueueItem = (orderObj, agentId, isCurrent) => {
    if (!orderObj) return null;
    const oId = orderObj.id;
    return (
      <div
        key={oId}
        className={`p-2 rounded-lg border text-sm ${isCurrent ? 'border-[#548B2E] bg-[#F1F8E9]' : 'border-gray-200 bg-gray-50'}`}
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-semibold text-gray-800">#{oId}</span>
              {isCurrent && <span className="text-xs px-1.5 py-0.5 rounded bg-[#548B2E] text-white">Current</span>}
              <span className="text-xs text-gray-500">{orderObj.orderNumber || ''}</span>
            </div>
            <p className="text-xs text-gray-600 truncate mt-0.5">{orderObj.shippingAddress}</p>
          </div>
        </div>

        {reassignPickerOrderId === oId ? (
          <div className="mt-2 flex items-center gap-1">
            {renderAgentDropdown(reassignTargetId, setReassignTargetId, agentId)}
            <button
              onClick={() => handleReassign(oId, reassignTargetId)}
              disabled={actingOnOrderId === oId || !reassignTargetId}
              className="px-2 py-1 bg-[#548B2E] text-white rounded text-xs hover:bg-[#3f6b20] disabled:opacity-50"
            >
              Move
            </button>
            <button
              onClick={() => { setReassignPickerOrderId(null); setReassignTargetId(''); }}
              className="p-1 text-gray-500"
            >
              <XCircle className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-1">
            <button
              onClick={() => { setReassignPickerOrderId(oId); setReassignTargetId(''); }}
              className="px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded"
            >
              Reassign
            </button>
            <button
              onClick={() => handleUnassign(oId)}
              disabled={actingOnOrderId === oId}
              className="px-2 py-0.5 text-xs text-red-500 hover:bg-red-50 rounded"
            >
              {actingOnOrderId === oId ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Unassign'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderAgentCard = (agent) => {
    const load = agentLoads[agent.id];
    const used = load?.used ?? 0;
    const currentOrder = load?.currentOrder;
    const queue = load?.queue || [];

    return (
      <div key={agent.id} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-[#548B2E]" />
              <span className="font-semibold text-gray-800">{agent.name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                agent.availability === 'available' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
              }`}>
                {agent.availability === 'available' ? 'Available' : 'On Delivery'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{agent.phone} · {agent.vehicleType}</p>
          </div>
          <div className="text-right">
            <p className={`text-sm font-bold ${used >= MAX_PER_AGENT ? 'text-red-600' : 'text-gray-700'}`}>
              {used}/{MAX_PER_AGENT}
            </p>
            <p className="text-xs text-gray-500">orders</p>
          </div>
        </div>

        {used === 0 ? (
          <div className="p-3 bg-gray-50 rounded-lg text-center text-xs text-gray-500">
            No active deliveries
          </div>
        ) : (
          <div className="space-y-2">
            {currentOrder && renderAgentQueueItem(currentOrder, agent.id, true)}
            {queue.map(q => renderAgentQueueItem(q, agent.id, false))}
          </div>
        )}
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Order Assignment</h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Assign incoming orders to delivery agents. Each agent can carry up to {MAX_PER_AGENT} orders.
          </p>
        </div>
        <button
          onClick={refreshAll}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column — unassigned pool */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Package className="w-5 h-5 text-[#548B2E]" />
              Unassigned Orders
              <span className="text-sm font-normal text-gray-500">({unassigned.length})</span>
            </h2>
          </div>

          {(() => {
            if (loadingPool) return (
              <div className="p-6 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#548B2E] mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading…</p>
              </div>
            );
            if (unassigned.length === 0) return (
              <div className="p-8 text-center bg-gray-50 rounded-lg">
                <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No unassigned orders right now.</p>
                <p className="text-xs text-gray-400 mt-1">Scheduled orders appear closer to their delivery slot.</p>
              </div>
            );
            return (
              <div className="space-y-3 max-h-[70vh] overflow-y-auto">
                {unassigned.map((order, index) => renderUnassignedOrder(order, index))}
              </div>
            );
          })()}
        </div>

        {/* Right column — agents with load */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
            <Truck className="w-5 h-5 text-[#548B2E]" />
            Active Agents
            <span className="text-sm font-normal text-gray-500">({agents.length})</span>
          </h2>

          {(() => {
            if (loadingAgents) return (
              <div className="p-6 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#548B2E] mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading…</p>
              </div>
            );
            if (agents.length === 0) return (
              <div className="p-8 text-center bg-gray-50 rounded-lg">
                <Truck className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No active agents.</p>
              </div>
            );
            return (
              <div className="space-y-3 max-h-[70vh] overflow-y-auto">
                {agents.map(renderAgentCard)}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

OrderAssignment.propTypes = { newCount: PropTypes.number };
export default OrderAssignment;
